import { action, observable, computed, runInAction } from "mobx";
import RootStore from "../RootStore";
import { LoaderStore } from "../common";
import AccountStatementStore, {
	DetailsTypeEnum,
} from "./AccountStatementStore";
import { AccountStatementService } from "@services/account-statement/AccountStatementService";
import { AccStatementBetSlipEventInOffer } from "@data-types/account-statement/AccountStatementBetSlip";
import { TUserPaymentTransactionLimited } from "@data-types/account-statement/TUserPaymentTransactionLimited";
import { UserAccount } from "@data-types";
import { SGUID_EMPTY_CONST } from "@utils";
import { mapBetSlipOffers } from "@utils/my-bets/helpers";
import { getWallet } from "@utils/common/bettingAccountUtils";

export default class AccountStatementDetailsStore {
	rootStore: RootStore;
	loader: LoaderStore;
	accountStatementStore: AccountStatementStore;

	constructor(accountStatementStore: AccountStatementStore) {
		this.accountStatementStore = accountStatementStore;
		this.rootStore = this.accountStatementStore.rootStore;

		this.loader = new LoaderStore();
	}

	//#region observables
	@observable isActive: boolean = false;
	@observable slipDetails:
		| AccStatementBetSlipEventInOffer
		| TUserPaymentTransactionLimited
		| null = null;
	@observable detailsType: DetailsTypeEnum = 0;
	@observable currentIndex: number = 0;
	//#endregion observables

	@computed get pageNumber(): number | undefined {
		return this.accountStatementStore.tableData?.page;
	}

	@computed get totalRecords(): number | undefined {
		return this.accountStatementStore.tableData?.totalRecords;
	}

	@computed get recordsPerPage(): number | undefined {
		return this.accountStatementStore.tableData?.recordsPerPage;
	}

	@computed get totalPages(): number {
		if (this.totalRecords && this.recordsPerPage) {
			const remainder = this.totalRecords % this.recordsPerPage;
			const totalPages =
				remainder === 0
					? this.totalRecords / this.recordsPerPage
					: (this.totalRecords - remainder) / this.recordsPerPage + 1;

			return totalPages;
		} else {
			return 1;
		}
	}

	@computed get isFirst(): boolean {
		if (this.pageNumber === 1 && this.currentIndex === 0) {
			return true;
		} else {
			return false;
		}
	}

	@computed get isLast(): boolean {
		if (
			this.pageNumber === this.totalPages &&
			this.currentIndex === this.transactions.length - 1
		) {
			return true;
		} else {
			return false;
		}
	}

	@computed get transactions(): TransactionsFetchType[] {
		const mappedTransactions: TransactionsFetchType[] = [];
		this.accountStatementStore.tableData?.transactions.map(
			(transaction) => {
				let transactionId: string | null;
				let detailType: DetailsTypeEnum;
				let transactionTypeId: string;
				let transactionName: string;
                let transactionTypeAbrv: string;
				if (transaction.betSlipId != SGUID_EMPTY_CONST) {
					detailType = DetailsTypeEnum.BetSlipDetails;
					transactionId = transaction.betSlipId;
				} else {
					detailType = DetailsTypeEnum.TransactionDetails;
					transactionId = transaction.userPaymentTransactionId;
				}
				transactionTypeId = transaction.transactionTypeId;
				transactionName = transaction.transactionType?.name ?? "";
                transactionTypeAbrv = transaction.transactionType?.abrv ?? "";

				if (transactionId && detailType && transaction.hasDetails) {
					mappedTransactions.push({
						transactionId,
						transactionTypeId,
						detailType,
						transactionName,
                        transactionTypeAbrv
					});
				}
			}
		);

		return mappedTransactions;
	}

	//#region setters
	@action.bound
	setDetailsType(type: number) {
		this.detailsType = type;
	}
	//#endregion setters

	//#region actions

	@action.bound
	openModal(transactionId: string | null, typeId: string) {
		this.isActive = true;
		this.setCurrentTransaction(transactionId, typeId);
	}

	@action.bound
	closeModal() {
		this.isActive = false;
		this.onDispose();
	}

	@action.bound
	setCurrentTransaction(transactionId: string | null, typeId: string) {
		const foundTransaction = this.transactions.find((transaction) => {
			if (
				transaction.transactionId === transactionId &&
				transaction.transactionTypeId === typeId
			) {
				return transaction;
			}
		});

		if (foundTransaction) {
			this.fetchSlipDetails(foundTransaction);

			runInAction(() => {
				this.currentIndex = this.transactions.indexOf(foundTransaction);
			});
		}
	}

	private async fetchBetSlipDetails(
		transactionId: string,
		wallet: UserAccount | null,
		transactionName: string,
        transactionTypeAbrv: string
	) {
		try {
			this.loader.suspend();
			const response = await AccountStatementService.getSlipDetails(
				transactionId,
				wallet
			);
			if (response) {
				mapBetSlipOffers(response);
				this.slipDetails = {
					...response,
					type: DetailsTypeEnum.BetSlipDetails,
					transactionName,
                    transactionTypeAbrv
				};
			}
		} catch (error) {
			console.error(error);
			this.slipDetails = null;
		} finally {
			this.detailsType = DetailsTypeEnum.BetSlipDetails;
			this.loader.resume();
		}
	}

	private async fetchTransactionDetails(
		transactionId: string,
		wallet: UserAccount | null,
		transactionName: string
	) {
		try {
			this.loader.suspend();
			const response =
				await AccountStatementService.getTransactionDetails(
					transactionId,
					wallet
				);

			if (response) {
				this.slipDetails = {
					...response,
					type: DetailsTypeEnum.TransactionDetails,
					transactionName,
				};
			}
		} catch (error) {
			this.slipDetails = null;
		} finally {
			this.detailsType = DetailsTypeEnum.TransactionDetails;
			this.loader.resume();
		}
	}

	@action.bound
	async fetchSlipDetails(transaction: {
		transactionId: string;
		detailType: DetailsTypeEnum;
		transactionName: string;
        transactionTypeAbrv: string
	}) {
		const wallet =
			getWallet(
				this.accountStatementStore.statementFilter?.shopId,
				this.accountStatementStore.statementFilter
					?.bettingAccountTypeAbrv,
				this.rootStore.userAuthStore.availableWallets
			) || null;

		const { detailType, transactionId, transactionName, transactionTypeAbrv } = transaction;

		if (detailType === DetailsTypeEnum.BetSlipDetails) {
			await this.fetchBetSlipDetails(
				transactionId,
				wallet,
				transactionName,
                transactionTypeAbrv
			);
		} else {
			await this.fetchTransactionDetails(
				transactionId,
				wallet,
				transactionName
			);
		}
	}

	@action.bound
	async moveRight() {
		if (
			this.currentIndex === this.transactions.length - 1 &&
			this.pageNumber
		) {
			await this.accountStatementStore.fetchTransactionsTable(
				this.pageNumber + 1
			);

			const nextTransaction = this.transactions[0];

			this.setCurrentTransaction(
				nextTransaction.transactionId,
				nextTransaction.transactionTypeId
			);
			return;
		}

		const nextTransaction = this.transactions[++this.currentIndex];
		this.setCurrentTransaction(
			nextTransaction.transactionId,
			nextTransaction.transactionTypeId
		);
	}

	@action.bound
	async moveLeft() {
		if (this.currentIndex === 0 && this.pageNumber) {
			await this.accountStatementStore.fetchTransactionsTable(
				this.pageNumber - 1
			);

			const nextTransaction =
				this.transactions[this.transactions.length - 1];

			this.setCurrentTransaction(
				nextTransaction.transactionId,
				nextTransaction.transactionTypeId
			);
			return;
		}

		const nextTransaction = this.transactions[this.currentIndex - 1];
		this.setCurrentTransaction(
			nextTransaction.transactionId,
			nextTransaction.transactionTypeId
		);
	}
	//#endregion actions

	//#region dispose
	@action.bound
	onDispose() {
		this.slipDetails = null;
		this.detailsType = 0;
		this.currentIndex = 0;
	}
	//#endregion dispose
}

export type TransactionsFetchType = {
	transactionId: string;
	transactionTypeId: string;
	detailType: DetailsTypeEnum;
	transactionName: string;
    transactionTypeAbrv: string
};
